import { useState, useMemo } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Percent, Plus, X, Trash2, Loader2, Search } from 'lucide-react'
import toast from 'react-hot-toast'
import { api } from '@/lib/api'
import { pedirExclusao } from '@/lib/confirmarExclusao'

export default function CampanhasDescontoPage() {
  const qc = useQueryClient()
  const [editando, setEditando] = useState<any | null>(null)

  const { data: campanhas = [], isLoading } = useQuery<any[]>({
    queryKey: ['campanhas-desconto'],
    queryFn: async () => (await api.get('/comercial/campanhas')).data,
  })
  const excluir = useMutation({
    mutationFn: async (id: number) => (await api.delete(`/comercial/campanhas/${id}`)).data,
    onSuccess: () => { toast.success('Campanha excluída'); qc.invalidateQueries({ queryKey: ['campanhas-desconto'] }) },
  })
  const hoje = new Date().toISOString().split('T')[0]

  return (
    <div className="p-4 space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-bold flex items-center gap-2"><Percent size={22} /> Campanha de Descontos</h1>
        <button onClick={() => setEditando({ nome: '', data_inicio: hoje, data_fim: hoje, percentual: '', tipo_desconto: 'comercial', clientes_ids: [], rotas_ids: [], tabelas_ids: [], produtos_ids: [], ativo: true })}
          className="flex items-center gap-2 px-4 py-2 rounded-lg bg-brand-600 hover:bg-brand-700 text-white text-sm font-medium">
          <Plus size={16} /> Nova campanha
        </button>
      </div>

      {isLoading ? <div className="p-8 text-center text-ink-500">Carregando…</div> : campanhas.length === 0 ? (
        <div className="card card-body text-center text-ink-500">Nenhuma campanha. Crie uma.</div>
      ) : (
        <div className="table-wrap">
          <table className="table text-sm">
            <thead><tr><th>Campanha</th><th>Período</th><th className="text-right">%</th><th>Tipo</th><th>Escopo</th><th></th></tr></thead>
            <tbody>
              {campanhas.map((c) => {
                const vig = c.data_fim >= hoje && c.data_inicio <= hoje && c.ativo
                const esc = []
                if (c.clientes_ids?.length) esc.push(`${c.clientes_ids.length} cliente(s)`)
                if (c.rotas_ids?.length) esc.push(`${c.rotas_ids.length} rota(s)`)
                if (c.tabelas_ids?.length) esc.push(`${c.tabelas_ids.length} tabela(s)`)
                if (c.produtos_ids?.length) esc.push(`${c.produtos_ids.length} produto(s)`)
                return (
                  <tr key={c.id} className="cursor-pointer hover:bg-ink-50" onClick={() => setEditando({ ...c })}>
                    <td className="font-medium">{c.nome} {vig && <span className="ml-1 text-xs text-emerald-700">● vigente</span>}</td>
                    <td>{new Date(c.data_inicio).toLocaleDateString('pt-BR')} – {new Date(c.data_fim).toLocaleDateString('pt-BR')}</td>
                    <td className="text-right font-bold">{c.percentual}%</td>
                    <td>{c.tipo_desconto}</td>
                    <td className="text-xs text-ink-500">{esc.join(' · ') || 'Todos'}</td>
                    <td className="text-right"><button onClick={(e) => { e.stopPropagation(); pedirExclusao({ descricao: 'esta campanha', onConfirm: () => excluir.mutate(c.id) }) }} className="text-red-500"><Trash2 size={14} /></button></td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}

      {editando && <ModalCampanha campanha={editando} onClose={() => setEditando(null)} onSaved={() => { setEditando(null); qc.invalidateQueries({ queryKey: ['campanhas-desconto'] }) }} />}
    </div>
  )
}

function ModalCampanha({ campanha, onClose, onSaved }: { campanha: any; onClose: () => void; onSaved: () => void }) {
  const [f, setF] = useState<any>(campanha)
  const set = (patch: any) => setF((s: any) => ({ ...s, ...patch }))

  const salvar = useMutation({
    mutationFn: async () => {
      const body = { ...f, percentual: Number(f.percentual) || 0, qtd_min: f.qtd_min ? Number(f.qtd_min) : null, qtd_max: f.qtd_max ? Number(f.qtd_max) : null }
      if (f.id) return (await api.put(`/comercial/campanhas/${f.id}`, body)).data
      return (await api.post('/comercial/campanhas', body)).data
    },
    onSuccess: () => { toast.success('Campanha salva'); onSaved() },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Erro ao salvar'),
  })

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-3xl max-h-[92vh] overflow-auto">
        <div className="flex items-center justify-between px-5 py-3 border-b sticky top-0 bg-white z-10">
          <h2 className="font-bold">{f.id ? 'Editar' : 'Nova'} campanha de desconto</h2>
          <button onClick={onClose}><X size={20} /></button>
        </div>
        <div className="p-5 space-y-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="col-span-2"><label className="label">Nome</label>
              <input className="input" value={f.nome} onChange={(e) => set({ nome: e.target.value })} /></div>
            <div><label className="label">% Desconto</label>
              <input type="number" step="0.01" className="input" value={f.percentual} onChange={(e) => set({ percentual: e.target.value })} /></div>
            <div><label className="label">Tipo</label>
              <select className="input" value={f.tipo_desconto} onChange={(e) => set({ tipo_desconto: e.target.value })}>
                <option value="comercial">Comercial</option><option value="financeiro">Financeiro</option>
              </select></div>
            <div><label className="label">Início</label>
              <input type="date" className="input" value={f.data_inicio} onChange={(e) => set({ data_inicio: e.target.value })} /></div>
            <div><label className="label">Fim</label>
              <input type="date" className="input" value={f.data_fim} onChange={(e) => set({ data_fim: e.target.value })} /></div>
            <div><label className="label">Qtd mín (opc)</label>
              <input type="number" step="0.001" className="input" value={f.qtd_min || ''} onChange={(e) => set({ qtd_min: e.target.value })} /></div>
            <div><label className="label">Qtd máx (opc)</label>
              <input type="number" step="0.001" className="input" value={f.qtd_max || ''} onChange={(e) => set({ qtd_max: e.target.value })} /></div>
          </div>

          <p className="text-xs text-ink-500">Escopo: deixe vazio = aplica a <strong>todos</strong>. Preencha pra restringir.</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <MultiPicker titulo="Tabelas de preço" endpoint="/comercial/tabelas-preco" label={(x) => x.nome} ids={f.tabelas_ids} onChange={(ids) => set({ tabelas_ids: ids })} />
            <MultiPicker titulo="Rotas" endpoint="/expedicao/rotas" label={(x) => x.nome} ids={f.rotas_ids} onChange={(ids) => set({ rotas_ids: ids })} />
            <MultiPicker titulo="Produtos" endpoint="/cadastros/produtos" params={{ limit: 1000 }} label={(x) => `${x.codigo} · ${x.descricao}`} ids={f.produtos_ids} onChange={(ids) => set({ produtos_ids: ids })} busca />
            <MultiPicker titulo="Clientes" endpoint="/cadastros/pessoas" params={{ tipo: 'cliente' }} label={(x) => x.nome} ids={f.clientes_ids} onChange={(ids) => set({ clientes_ids: ids })} busca />
          </div>
        </div>
        <div className="flex justify-end gap-2 px-5 py-3 border-t sticky bottom-0 bg-white">
          <button onClick={onClose} className="btn-secondary">Cancelar</button>
          <button onClick={() => salvar.mutate()} disabled={salvar.isPending || !f.nome}
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-brand-600 hover:bg-brand-700 text-white text-sm font-medium disabled:opacity-50">
            {salvar.isPending ? <Loader2 className="animate-spin" size={16} /> : null} Salvar
          </button>
        </div>
      </div>
    </div>
  )
}

function MultiPicker({ titulo, endpoint, params, label, ids, onChange, busca }: {
  titulo: string; endpoint: string; params?: any; label: (x: any) => string; ids: number[]; onChange: (ids: number[]) => void; busca?: boolean
}) {
  const [q, setQ] = useState('')
  const { data: opcoes = [] } = useQuery<any[]>({
    queryKey: ['picker', endpoint, JSON.stringify(params || {})],
    queryFn: async () => (await api.get(endpoint, { params })).data,
  })
  const lista = useMemo(() => {
    const ql = q.toLowerCase()
    return (opcoes || []).filter((o) => !ql || label(o).toLowerCase().includes(ql)).slice(0, 200)
  }, [opcoes, q])
  const toggle = (id: number) => onChange(ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id])

  return (
    <div className="border rounded-lg p-2">
      <div className="text-xs font-semibold text-ink-500 uppercase mb-1">{titulo} {ids.length > 0 && <span className="text-brand-600">({ids.length})</span>}</div>
      {busca && (
        <div className="relative mb-1">
          <Search size={13} className="absolute left-2 top-2 text-ink-400" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar…" className="input !pl-7 !py-1 text-xs w-full" />
        </div>
      )}
      <div className="max-h-40 overflow-auto space-y-0.5">
        {lista.map((o) => (
          <label key={o.id} className="flex items-center gap-2 text-sm px-1 py-0.5 hover:bg-ink-50 rounded cursor-pointer">
            <input type="checkbox" checked={ids.includes(o.id)} onChange={() => toggle(o.id)} />
            <span className="truncate">{label(o)}</span>
          </label>
        ))}
      </div>
    </div>
  )
}
